import type { ShallowRef, WatchStopHandle } from "vue"

import type { BaseEntity, Entity, EntityProperty } from "./BaseEntity"
import type { StaticEntity } from "./StaticEntity"

import { PropertyId, PropertySchema } from "."

type Constructor<
  EP extends EntityProperty,
  E extends Entity<EP>,
> = abstract new (...args: any[]) => BaseEntity<EP, E>

export function withReactive<
  EP extends EntityProperty,
  E extends Entity<EP>,
  Static extends StaticEntity<EP, E>,
  BaseClass extends Constructor<EP, E>,
>(Base: BaseClass) {
  abstract class ReactiveEntity extends Base {
    abstract get _inner(): ShallowRef<Static> | WritableComputedRef<Static>
    abstract set _inner(value: ShallowRef<Static> | WritableComputedRef<Static>)
    abstract get _innerId(): string
    abstract set _innerId(s: string)
    abstract get location(): SelectionLocation
    abstract set location(location: SelectionLocation)

    /**
     * Constructs a _new_ static entity, which will guarantee trigger
     * reactivity hooks (whereas interior mutation of the static then
     * self assignment might not, since the assigned object is identical)
     */
    abstract intoStatic(): Static
    abstract refresh: () => void
    abstract use: () => this
    abstract dispose: () => void

    propertyEdit: Ref<{
      propertyId: PropertyId
      newValue: EntityPropertyValue
    } | undefined> = ref()

    ref = () => this._inner
    asStatic = () => this._inner.value
    inner = () => this._inner.value.inner()

    removeProperty(property: PropertyId): void {
      this.asStatic().removeProperty(property)
    }

    mergeWithNewer<T = Static>(later?: T): boolean {
      const newer = later as T | undefined
      if (!newer)
        return false
      const changed = this.asStatic().mergeWithNewer(newer)
      if (changed)
        this._inner.value = this.intoStatic()
      return changed
    }

    abstract updateProperty: <Req>(
      propertyId: PropertyId,
      newValue: EntityPropertyValue,
      deleted?: boolean,
      unversioned?: boolean,
    ) => { updated: Static, request: Req }

    abstract isAuthorizedToEditProperty: (propertyId?: PropertyId) => boolean

    isEditing = () => {
      return isDefined(this.propertyEdit.value)
    }

    clearEditingState = () => {
      this.propertyEdit.value = undefined
    }

    onRequestedPropertyEdit = ({ propertyId, location }: {
      propertyId: PropertyId
      location: SelectionLocation
    }) => {
      const property = this.property(propertyId)
      if (location !== this.location)
        return

      console.debug("Entity property edit requested", propertyId, location)

      if (!this.isAuthorizedToEditProperty(propertyId)) {
        console.warn("Not authorized to edit property", propertyId)
        return
      }

      let newValue: EntityPropertyValue
      if (property)
        newValue = clone(property.value) as EntityPropertyValue

      else if (propertyId === PropertyId.FILE_NOTES)
        newValue = { type: PropertySchema.AnyStringValue, anyString: "" }
      else {
        console.warn("Need to implement full list of default values")
        newValue = { type: PropertySchema.NonEmptyStringValue, nonEmptyString: "" }
      }

      if (isDefined(this.propertyEdit.value))
        console.debug("Overriding existing property edit", clone(this.propertyEdit.value))

      // TODO: stop editing existing property?
      // TODO: can we edit more than one property at a time?

      this.propertyEdit.value = {
        propertyId,
        newValue,
      }
    }

    /**
     * TODO: make this generic over entities by making the update function
     * generic
     */
    finishEditing = (
      deleted?: boolean,
      unversioned?: boolean,
    ) => {
      if (!this.propertyEdit.value)
        return

      const { newValue, propertyId } = this.propertyEdit.value!
      const oldValue = this.property(propertyId)?.value

      console.debug("Finishing editing property", this.property(propertyId), newValue)

      // Check if the value is different
      if (!newValue) {
        console.error("No new value to insert")
        return this.clearEditingState()
      }

      // trim whitespace from strings
      if (newValue.type === PropertySchema.AnyStringValue && isDefined(newValue.anyString)) {
        newValue.anyString = newValue.anyString.trim()
      }
      else if (newValue.type === PropertySchema.NonEmptyStringValue && isDefined(newValue.nonEmptyString)) {
        newValue.nonEmptyString = newValue.nonEmptyString.trim()
      }

      if (newValue.type === PropertySchema.NonEmptyStringValue && newValue.nonEmptyString === "") {
        console.warn("String is empty, reverting to old value")
        return this.clearEditingState()
      }
      if (deepEqual(oldValue, newValue)) {
        console.info("Old and new property values match, exiting", oldValue, newValue)
        return this.clearEditingState()
      }
      // Send an update command to update the value in the database
      const { updated } = this.updateProperty(
        propertyId as PropertyId,
        newValue,
        deleted,
        unversioned,
      )

      // set the properties locally
      this.mergeWithNewer(updated)

      // Now reset the editing state, which should cause no flicker as we updated the file
      // already!
      return this.clearEditingState()
    }

    /**
     * Creates a reactive map of files that automatically updates when items are added or
     * removed
     * @param targetFiles a ref of some kind (likely shallow ref) to a map of static files, that
     * could update over time to add or remove elements
     * @param init a initializer for creating a reactive file from a static file.
     * @returns a computed ref of the reactive files, that should not be
     * updated (update the original object instead)
     */
    static toReactiveMap = <R extends ReactiveEntity>(
      targetFiles: Ref<Record<string, Static>>,
      init: (file: Static) => R,
    ): {
      reactive: ShallowRef<Record<string, R>>
      unwatch: WatchStopHandle
    } => {
      const entities = shallowRef<Record<string, R>>({})
      const onFileSelectionUpdated = (
        newFiles: Record<string, Static> = {},
      ) => {
        let updated = false
        // all net new files need to be added
        for (const addedFile of Object.values(newFiles).filter(f => !entities.value[f.id])) {
          const reactiveFile = init(addedFile)
          entities.value[addedFile.id] = reactiveFile
          updated = true
        }
        // all files that are no longer present need to be removed
        for (const entityId of Object.keys(entities.value)) {
          if (newFiles[entityId])
            continue
          entities.value[entityId]?.dispose()
          delete entities.value[entityId]
          updated = true
        }

        if (updated)
          triggerRef(entities)
      }
      return {
        reactive: entities,
        unwatch: watchImmediate(targetFiles, onFileSelectionUpdated),
      }
    }
  }
  return ReactiveEntity
}
